import React, { useContext, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  IconButton,
  styled,
  useTheme,
  Box,
  Collapse,
  alpha,
} from "@mui/material";
import {
  Flight as FlightIcon,
  AirplanemodeActive as AirlinesIcon,
  Place as DestinationsIcon,
  Class as ClassesIcon,
  AttachMoney as FaresIcon,
  Campaign as PopupIcon,
  People as UsersIcon,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Logout,
  Flight,
  Brightness7,
  Brightness4,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUserAuth } from "../../components/context/UserAuthContext";
import { ThemeContext } from "../../index";

const drawerWidth = 240;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    width: drawerWidth,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    borderRight: "none",
    backgroundImage: "none",
  },
  "&.collapsed": {
    width: `calc(${theme.spacing(7)} + 1px)`,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    "& .MuiDrawer-paper": {
      width: `calc(${theme.spacing(7)} + 1px)`,
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
  },
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const menuItems = [
  {
    text: "Flights",
    icon: <Flight />,
    path: "/admin",
  },
  {
    text: "Users",
    icon: <UsersIcon />,
    path: "/admin/users",
  },
  {
    text: "Airline",
    icon: <AirlinesIcon />,
    path: "/admin/airline",
  },
  {
    text: "Locations",
    icon: <DestinationsIcon />,
    path: "/admin/locations",
  },
  {
    text: "Add Flight",
    icon: <FlightIcon />,
    path: "/admin/addflight",
  },
  {
    text: "Seasonal Fares",
    icon: <FaresIcon />,
    path: "/admin/seasonal_fares",
  },
  {
    text: "Pop Up",
    icon: <PopupIcon />,
    path: "/admin/pop_up",
  },
  {
    text: "Flight Classes",
    icon: <ClassesIcon />,
    path: "/admin/f_classes",
  },
  {
    text: "Register",
    icon: <SettingsIcon />,
    path: "/admin/register",
  },
];

const Sidebar = ({ open, handleDrawerToggle }) => {
  const { mode, toggleTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState({});

  const handleExpand = (text) => {
    setExpandedItems((prev) => ({
      ...prev,
      [text]: !prev[text],
    }));
  };

  const { logOut } = useUserAuth();

  const handleLogOut = async () => {
    try {
      await logOut();
      navigate("/login", { replace: true });
    } catch (err) {
      console.log(err.message);
    }
  };

  return (
    <StyledDrawer
      variant="permanent"
      open={open}
      className={!open ? "collapsed" : ""}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: theme.palette.background.paper,
          borderRight: "none",
        },
      }}
    >
      <DrawerHeader>
        <LogoContainer>
          <Box
            component={"img"}
            loading="eager"
            src="/images/logo.webp"
            alt="Logo"
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </LogoContainer>
        <IconButton onClick={handleDrawerToggle}>
          {open ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </DrawerHeader>
      <Divider />

      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <Tooltip title={!open ? item.text : ""} placement="right">
              <ListItem disablePadding sx={{ display: "block" }}>
                <ListItemButton
                  component={item.subItems ? "div" : Link}
                  to={!item.subItems ? item.path : null}
                  onClick={item.subItems ? () => handleExpand(item.text) : null}
                  selected={location.pathname === item.path}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&.Mui-selected": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color:
                        location.pathname === item.path
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                  {item.subItems &&
                    open &&
                    (expandedItems[item.text] ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    ))}
                </ListItemButton>
              </ListItem>
            </Tooltip>

            {item.subItems && open && (
              <Collapse
                in={expandedItems[item.text]}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.text}
                      component={Link}
                      to={subItem.path}
                      selected={location.pathname === subItem.path}
                      sx={{
                        pl: 6,
                        "&.Mui-selected": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.08
                          ),
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.15
                            ),
                          },
                        },
                      }}
                    >
                      <ListItemText
                        primary={subItem.text}
                        sx={{
                          "& span": {
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ mt: "auto" }}>
        <Tooltip title={!open ? "Toggle theme" : ""} placement="right">
          <ListItemButton
            onClick={() => toggleTheme()}
            sx={{
              minHeight: 48,
              justifyContent: open ? "initial" : "center",
              px: 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : "auto",
                justifyContent: "center",
                color: theme.palette.text.secondary,
              }}
            >
              {mode === "light" ? (
                <Brightness7 sx={{ color: "warning.main" }} />
              ) : (
                <Brightness4 sx={{ color: "primary.main" }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={`Switch to ${mode === "light" ? "Dark" : "Light"} Mode`}
              sx={{ opacity: open ? 1 : 0 }}
            />
          </ListItemButton>
        </Tooltip>
        <Divider />
        <Tooltip title={!open ? "Settings" : ""} placement="right">
          <ListItemButton
            onClick={handleLogOut}
            sx={{
              minHeight: 48,
              justifyContent: open ? "initial" : "center",
              px: 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : "auto",
                justifyContent: "center",
                color: theme.palette.text.secondary,
              }}
            >
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Logout" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </Tooltip>
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar;
